import { cn } from "@/utils";
import type React from "react";

/**
 * Full-screen shell for the focused native mobile surfaces (home, voice).
 *
 * The mobile screens are hosted inside the normal app layout (canvas-centric
 * WorkspaceLayout, or the legacy CohesiveLayout) so they inherit its providers
 * — file-conflict resolver, upload queue, toasts, session context. This wrapper
 * lifts them *above* that layout's desktop chrome (rails, header, tab bar) with
 * a fixed overlay, giving a clean phone-first surface while the full app stays
 * mounted underneath and one "Open full app" tap away.
 *
 * `underAppHeader` keeps the shared WorkspaceAppHeader (Workspaces/Skills/
 * Connectors nav + utilities) visible and functional: the overlay starts below
 * the header block (`--app-header-block`) and drops to a low z-index so the
 * header (z-20) and the rail drawer (z-30/40) render above it. Default (voice,
 * legacy launcher) stays a top-to-bottom z-40 overlay that hides the header.
 */
export function MobileScreen({
  children,
  className,
  underAppHeader = false,
}: {
  children: React.ReactNode;
  className?: string;
  underAppHeader?: boolean;
}) {
  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 flex flex-col overflow-y-auto bg-background px-4",
        underAppHeader
          ? "top-[var(--app-header-block)] z-10"
          : "top-0 z-40 pt-safe",
        className,
      )}
    >
      {children}
    </div>
  );
}

export default MobileScreen;
